import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ErrorDetailModal } from './ErrorDetailModal';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/ClerkAuthContext';

interface ErrorGroup {
  id: number;
  organization_id: string;
  fingerprint_primary: string;
  fingerprint_secondary: string;
  error_type: string;
  representative_message: string;
  representative_stack: string[];
  first_seen_at: string;
  last_seen_at: string;
  occurrence_count: number;
  status: 'active' | 'resolved' | 'ignored';
  resolved_at?: string;
  created_at: string;
}

interface ErrorPanelProps {
  appId?: string;
}

export const ErrorPanel: React.FC<ErrorPanelProps> = ({ appId }) => {
  const { getToken } = useAuth();
  const [errorGroups, setErrorGroups] = useState<ErrorGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<ErrorGroup | null>(null);
  const [statusFilter, setStatusFilter] = useState<'active' | 'resolved' | 'ignored'>('active');

  useEffect(() => {
    fetchErrorGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchErrorGroups = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      // Use app-scoped API if appId is provided, otherwise use org-level API
      const response = appId
        ? await apiService.getErrorGroupsByApp(token, appId, statusFilter)
        : await apiService.getErrorGroups(token, statusFilter);

      setErrorGroups(response.groups || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch error groups:', err);
      setError('Failed to load errors');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-red-100 text-red-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'ignored': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="p-4 bg-red-50 border-red-200">
          <p className="text-red-800">{error}</p>
          <Button onClick={fetchErrorGroups} className="mt-2">
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Error Monitoring</h2>
        <div className="flex gap-2">
          {(['active', 'resolved', 'ignored'] as const).map(status => (
            <Button
              key={status}
              variant={statusFilter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(status)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {errorGroups.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="text-gray-400 mb-2">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-600 font-medium">No {statusFilter} errors</p>
          <p className="text-gray-500 text-sm mt-1">
            {statusFilter === 'active'
              ? 'Your application is running smoothly'
              : `No ${statusFilter} errors to display`}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {errorGroups.map(group => (
            <Card
              key={group.id}
              className="p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedGroup(group)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={getStatusColor(group.status)}>
                      {group.status}
                    </Badge>
                    <span className="text-sm font-mono text-gray-600">{group.error_type}</span>
                    <Badge variant="outline" className="text-xs">
                      {group.occurrence_count} {group.occurrence_count === 1 ? 'occurrence' : 'occurrences'}
                    </Badge>
                  </div>
                  <p className="text-gray-900 font-medium mb-1 truncate">
                    {group.representative_message}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>First seen {formatTimestamp(group.first_seen_at)}</span>
                    <span>•</span>
                    <span>Last seen {formatTimestamp(group.last_seen_at)}</span>
                  </div>
                </div>
                <Button variant="ghost" size="sm">
                  Details →
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {selectedGroup && (
        <ErrorDetailModal
          group={selectedGroup}
          appId={appId}
          onClose={() => setSelectedGroup(null)}
          onRefresh={fetchErrorGroups}
        />
      )}
    </div>
  );
};
